import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSpring, animated, SpringValue } from "@react-spring/web";
import { LayerGroup, LayerGroupName } from "types/dataModel";
import { ReactComponent as LinkSvg } from "assets/link-icon.svg";
import nbsIcon from "assets/nbs.svg";
import downloads from "data/downloads";
import SearchBar, { Bounds } from "panels/home-info-panel/search-bar";
import { Icon } from "@iconify/react";
import {
  useLayerBounceContext,
  LayerSelectionFrom,
  LAYER_BOUNCE_FLASH_TIMEOUT,
  LAYER_BOUNCE_OPEN_TIMEOUT,
} from "layers/layer-bounce";
import "panels/layer-selection/layer-selection.css";
import QuickExplore from "panels/quick-explore";
import { useAnalyticsHandler } from "hooks/useAnalytics";
import { useMobileContext } from "contexts/MobileContext";
import { useIsTouchDevice } from "hooks/useIsTouchDevice";
import { Headline, Text } from "components/typography";

type LayerSelectionProps = {
  layerGroups: Record<LayerGroupName, LayerGroup>;
  selectedLayer: LayerGroupName;
  setSelectedLayer: (layer: LayerGroupName) => void;
  setBounds: (bounds: Bounds) => void;
};

const ICON_CONTAINER_INITIAL_WIDTH = 55;
const ICON_INITIAL_SIZE = 30;
const ICON_HOVER_SIZE = 35;

export const LayerSelection: React.FC<LayerSelectionProps> = ({
  layerGroups,
  selectedLayer,
  setSelectedLayer,
  setBounds,
}) => {
  const layerSelectedHandler = useAnalyticsHandler("Layer Selected");
  const linkOpenedHandler = useAnalyticsHandler("Link Opened");
  const dataDownloadedHandler = useAnalyticsHandler("Data Downloaded");
  const { layerGroupSelectedFrom, setLayerGroupSelectedFrom } =
    useLayerBounceContext();
  const layerSelectionContainer = useRef<HTMLDivElement>(null);
  const activeLayerButton = useRef<HTMLButtonElement>(null);
  const [containerSprings, containerApi] = useSpring(() => ({
    from: { width: ICON_CONTAINER_INITIAL_WIDTH },
  }));
  const [iconSprings, iconApi] = useSpring(() => ({
    from: { width: ICON_INITIAL_SIZE, height: ICON_INITIAL_SIZE },
  }));

  // Fixed spring configuration for SEARCH item to prevent animation interference
  const [searchIconSprings] = useSpring(() => ({
    from: { width: ICON_INITIAL_SIZE, height: ICON_INITIAL_SIZE },
    config: { duration: 0 }, // No animation
  }));

  // Mobile context
  const { isLayerSelectionExpanded, setIsLayerSelectionExpanded } =
    useMobileContext();

  const isTouchDevice = useIsTouchDevice();

  useEffect(() => {
    if (isTouchDevice && isLayerSelectionExpanded) {
      // On mobile, let CSS handle the width (75vw), only animate icons
      iconApi.start({ width: ICON_HOVER_SIZE, height: ICON_HOVER_SIZE });
    } else if (isTouchDevice && !isLayerSelectionExpanded) {
      // On mobile collapsed, let CSS handle the width transition, only animate icons
      iconApi.start({ width: ICON_INITIAL_SIZE, height: ICON_INITIAL_SIZE });
    } else {
      // Desktop behavior
      containerApi.start({ width: ICON_CONTAINER_INITIAL_WIDTH });
      iconApi.start({ width: ICON_INITIAL_SIZE, height: ICON_INITIAL_SIZE });
    }
  }, [containerApi, iconApi, isTouchDevice, isLayerSelectionExpanded]);

  const handleIconHover = useCallback(() => {
    if (!isTouchDevice) {
      containerApi.start({ width: activeLayerButton.current?.scrollWidth });
      iconApi.start({ width: ICON_HOVER_SIZE, height: ICON_HOVER_SIZE });
    }
  }, [containerApi, iconApi, isTouchDevice]);

  const handleIconUnhover = useCallback(() => {
    if (!isTouchDevice) {
      containerApi.start({ width: ICON_CONTAINER_INITIAL_WIDTH });
      iconApi.start({ width: ICON_INITIAL_SIZE, height: ICON_INITIAL_SIZE });
    }
  }, [containerApi, iconApi, isTouchDevice]);

  useEffect(() => {
    if (layerGroupSelectedFrom === LayerSelectionFrom.breadcrumb) {
      handleIconHover();
      setTimeout(() => {
        handleIconUnhover();
      }, LAYER_BOUNCE_OPEN_TIMEOUT); //This is the length of time to open the panel
      setTimeout(() => {
        setLayerGroupSelectedFrom(LayerSelectionFrom.reset);
      }, LAYER_BOUNCE_FLASH_TIMEOUT); //This should correspond to whatever the CSS animation time is, or it will be cut short
    }
  }, [
    handleIconHover,
    handleIconUnhover,
    layerGroupSelectedFrom,
    setLayerGroupSelectedFrom,
  ]);

  const selectedLayerValid = Object.values(layerGroups).some(
    (layerGroup) => layerGroup.name === selectedLayer,
  );

  return (
    <animated.div
      className={`layer-selection absolute z-[02] top-[12px] left-[12px] bg-white overflow-hidden rounded-[12px] shadow-xl ${
        isTouchDevice
          ? `mobile ${isLayerSelectionExpanded ? "expanded" : ""}`
          : ""
      }`}
      onMouseEnter={handleIconHover}
      onMouseLeave={handleIconUnhover}
      ref={layerSelectionContainer}
      style={isTouchDevice ? {} : { ...containerSprings }}
    >
      {/* NBS Icon - hidden on mobile */}
      {!isTouchDevice && (
        <div className="bg-white block text-left w-[25rem] nbs-icon-container">
          <div
            className="grid grid-flow-col gap-3 py-2"
            style={{
              gridTemplateColumns: `${ICON_CONTAINER_INITIAL_WIDTH}px 1fr`,
            }}
          >
            <div
              className="self-center pl-3"
              style={{ width: ICON_CONTAINER_INITIAL_WIDTH, flexShrink: 0 }}
            >
              <div
                style={{
                  width: ICON_INITIAL_SIZE,
                  height: ICON_INITIAL_SIZE,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <img
                  src={nbsIcon}
                  alt="NBS"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                />
              </div>
            </div>
            <div className="self-center pr-3" style={{ minWidth: 0 }}>
              <Headline
                level={4}
                className="nbs-title uppercase font-semibold text-[#013E74]"
              >
                Coastal Resilience Explorer
              </Headline>
            </div>
          </div>
        </div>
      )}
      {Object.values(layerGroups).map((layerGroup, index) => (
        <button
          key={index}
          className={`${
            layerGroup.name === selectedLayer
              ? "bg-secondary-blue selected"
              : "bg-white"
          } ${
            !isTouchDevice ? "hover:bg-secondary-blue" : ""
          } block text-left transition-[height] w-[25rem] ${
            layerGroupSelectedFrom === LayerSelectionFrom.breadcrumb && "bounce"
          } ${
            isTouchDevice &&
            !isLayerSelectionExpanded &&
            layerGroup.name !== selectedLayer
              ? "hidden"
              : ""
          }`}
          onClick={(e) => {
            e.stopPropagation();
            if (isTouchDevice) {
              // If panel is collapsed, expand it
              if (!isLayerSelectionExpanded) {
                setIsLayerSelectionExpanded(true);
              } else {
                // If panel is expanded, select the layer and collapse
                setSelectedLayer(layerGroup.name);
                setLayerGroupSelectedFrom(
                  LayerSelectionFrom.layerSelectionPanel,
                );
                layerSelectedHandler({
                  layerGroup: layerGroup.name,
                });
                setIsLayerSelectionExpanded(false);
              }
            } else {
              // Desktop behavior - just select the layer
              setSelectedLayer(layerGroup.name);
              setLayerGroupSelectedFrom(LayerSelectionFrom.layerSelectionPanel);
              layerSelectedHandler({
                layerGroup: layerGroup.name,
              });
            }
          }}
          ref={
            layerGroup.name === selectedLayer ||
            (!selectedLayerValid && index === 0)
              ? activeLayerButton
              : null
          }
        >
          <MenuItem
            iconSprings={iconSprings}
            name={layerGroup.name}
            IconComponent={layerGroup.IconComponent}
            description={layerGroup.shortDescription}
            showInfoIcon={isTouchDevice}
            allowClickToExpand={false}
            isMobileCollapsed={isTouchDevice && !isLayerSelectionExpanded}
          />
        </button>
      ))}

      {/* Show Data menu item on desktop or when expanded on mobile */}
      {(!isTouchDevice || isLayerSelectionExpanded) && (
        <div
          className={`bg-white ${
            !isTouchDevice ? "hover:bg-secondary-blue" : ""
          } block text-left transition-[height] w-[25rem]`}
        >
          <MenuItem
            iconSprings={iconSprings}
            name="Data"
            isMobileCollapsed={isTouchDevice && !isLayerSelectionExpanded}
            description={
              <>
                <Text level={4}>
                  {downloads.map((download) => (
                    <a
                      key={download.url}
                      href={download.url}
                      target="_blank"
                      rel="noreferrer"
                      className="flex row items-center gap-2 text-primary-blue hover:text-secondary-orange hover:stroke-secondary-orange hover:fill-secondary-orange"
                      onClick={() => {
                        dataDownloadedHandler({
                          description: download.description,
                          url: download.url,
                        });
                      }}
                    >
                      {download.description}
                      {download.icon}
                    </a>
                  ))}
                </Text>
                <Headline level={4} className="py-2">
                  Links
                </Headline>
                <Text level={4}>
                  <a
                    href="https://www.coastalresiliencelab.org/"
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary-blue hover:text-secondary-orange hover:stroke-secondary-orange hover:fill-secondary-orange"
                    onClick={() => {
                      linkOpenedHandler({
                        linkType: "website",
                        linkName:
                          "Discover the Center for Coastal Climate Resilience",
                        linkUrl: "https://www.coastalresiliencelab.org/",
                      });
                    }}
                  >
                    Discover the Center for Coastal Climate Resilience&nbsp;–›
                  </a>
                </Text>
                <Text level={4}>
                  <a
                    href="https://doi.org/10.1038/s41598-020-61136-6"
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary-blue hover:text-secondary-orange hover:stroke-secondary-orange hover:fill-secondary-orange"
                    onClick={() => {
                      linkOpenedHandler({
                        linkType: "paper",
                        linkName: "Read the scientific paper",
                        linkUrl: "https://doi.org/10.1038/s41598-020-61136-6",
                      });
                    }}
                  >
                    Read the publication&nbsp;–›
                  </a>
                </Text>
              </>
            }
            showInfoIcon={false}
          />
        </div>
      )}
      {!isTouchDevice && (
        <div
          className={`bg-white ${
            !isTouchDevice ? "hover:bg-secondary-blue" : ""
          } block text-left transition-[height] w-[25rem]`}
        >
          <MenuItem
            iconSprings={iconSprings}
            name="Quick Explore"
            description={() => <QuickExplore />}
            IconComponent={() => (
              <Icon
                icon="material-symbols:explore-outline"
                color="#003c6c"
                className="w-full h-full"
              />
            )}
            showInfoIcon={false}
            isMobileCollapsed={isTouchDevice && !isLayerSelectionExpanded}
          />
        </div>
      )}
      {typeof window.google !== "undefined" && !isTouchDevice && (
        <div
          className={`bg-white ${
            !isTouchDevice ? "hover:bg-secondary-blue" : ""
          } block text-left transition-[height] w-[25rem]`}
        >
          <MenuItem
            iconSprings={searchIconSprings}
            name="Search"
            className="search-menu-item"
            description={(updateHeight, hovered) => (
              <SearchBar
                setBounds={setBounds}
                updateHeight={updateHeight}
                hovered={hovered}
              />
            )}
            IconComponent={() => (
              <Icon
                icon="material-symbols:search"
                color="#003c6c"
                className="w-full h-full"
                data-search-icon="true"
              />
            )}
            showInfoIcon={false}
            isMobileCollapsed={isTouchDevice && !isLayerSelectionExpanded}
          />
        </div>
      )}
    </animated.div>
  );
};

export type UpdateHeightFunc = (height?: number) => void;

const MenuItem: React.FC<{
  iconSprings: {
    width: SpringValue<number>;
    height: SpringValue<number>;
  };
  name: string | React.ReactNode;
  IconComponent?: React.FC<React.SVGProps<SVGSVGElement>>;
  description:
    | string
    | React.ReactNode
    | ((updateHeight: UpdateHeightFunc, hovered: boolean) => React.ReactNode);
  showInfoIcon?: boolean;
  className?: string;
  allowClickToExpand?: boolean;
  isMobileCollapsed?: boolean;
}> = ({
  iconSprings,
  name,
  IconComponent,
  description,
  showInfoIcon = true,
  className = "",
  allowClickToExpand = true,
  isMobileCollapsed = false,
}) => {
  const descriptionRef = useRef<HTMLParagraphElement>(null);
  const [hovered, setHovered] = useState(false);
  const [infoExpanded, setInfoExpanded] = useState(false);
  const [descriptionSprings, descriptionApi] = useSpring(() => ({
    from: { height: 0 },
  }));

  const isTouchDevice = useIsTouchDevice();

  const handleCopyHover = () => {
    // Only expand on hover for desktop (non-touch) devices
    if (!isTouchDevice && !infoExpanded) {
      descriptionApi.start({ height: descriptionRef.current?.scrollHeight });
      setHovered(true);
    }
  };

  const handleCopyUnhover = () => {
    // Only collapse on unhover for desktop (non-touch) devices
    if (!isTouchDevice && !infoExpanded) {
      setTimeout(() => {
        descriptionApi.start({ height: 0 });
        setHovered(false);
      }, 200);
    }
  };

  const handleInfoClick = () => {
    setInfoExpanded(!infoExpanded);
  };

  // Handle info expansion animation
  useEffect(() => {
    if (infoExpanded) {
      descriptionApi.start({ height: descriptionRef.current?.scrollHeight });
      setHovered(true);
    } else {
      descriptionApi.start({ height: 0 });
      setHovered(false);
    }
  }, [infoExpanded, descriptionApi]);

  const updateHeight = useCallback<UpdateHeightFunc>(
    (height) => {
      descriptionApi.start({
        height: height ? height : descriptionRef.current?.scrollHeight,
      });
    },
    [descriptionApi],
  );

  const isDescriptionExpanded =
    !isMobileCollapsed && description && (hovered || infoExpanded);

  return (
    <div
      className={`${className} ${isDescriptionExpanded ? "pb-1" : ""}`}
      onMouseEnter={handleCopyHover}
      onMouseLeave={handleCopyUnhover}
    >
      <div
        className="grid grid-flow-col gap-3 py-2"
        style={{
          gridTemplateColumns: isMobileCollapsed
            ? `${ICON_CONTAINER_INITIAL_WIDTH}px`
            : `${ICON_CONTAINER_INITIAL_WIDTH}px 1fr`,
        }}
        onClick={
          allowClickToExpand && !showInfoIcon && description
            ? (e) => {
                e.stopPropagation();
                handleInfoClick();
              }
            : undefined
        }
      >
        <div
          className="self-center pl-3"
          style={{ width: ICON_CONTAINER_INITIAL_WIDTH, flexShrink: 0 }}
        >
          <div
            style={{
              width: ICON_INITIAL_SIZE,
              height: ICON_INITIAL_SIZE,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {IconComponent ? (
              <IconComponent fill="#003c6c" />
            ) : (
              <LinkSvg
                color="#003c6c"
                width="83%"
                height="83%"
                className="mx-auto"
              />
            )}
          </div>
        </div>
        {!isMobileCollapsed && (
          <div className="self-center pr-3" style={{ minWidth: 0 }}>
            {description && showInfoIcon ? (
              <div className="flex items-center justify-between">
                <Headline
                  level={4}
                  className={`text-neutral-midfi-grey ${
                    allowClickToExpand && !showInfoIcon && description
                      ? "cursor-pointer"
                      : ""
                  }`}
                >
                  {name}
                </Headline>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleInfoClick();
                  }}
                  className="flex items-center justify-center text-neutral-midfi-grey/70 hover:text-neutral-midfi-grey transition-colors"
                  title="More information"
                >
                  <Icon icon="material-symbols:info" width="1em" height="1em" />
                </button>
              </div>
            ) : (
              <Headline
                level={4}
                className={`text-neutral-midfi-grey ${
                  allowClickToExpand && !showInfoIcon && description
                    ? "cursor-pointer"
                    : ""
                }`}
              >
                {name}
              </Headline>
            )}
          </div>
        )}
      </div>
      {!isMobileCollapsed && description && typeof description === "string" ? (
        <animated.div
          className="w-full overflow-hidden pr-3"
          style={{
            ...descriptionSprings,
            paddingLeft: `${ICON_CONTAINER_INITIAL_WIDTH + 12}px`, // icon column width + gap-3
          }}
          ref={descriptionRef}
        >
          <Text level={3}>{description}</Text>
        </animated.div>
      ) : (
        !isMobileCollapsed && (
          <animated.div
            className="w-full overflow-hidden pr-3"
            style={{
              ...descriptionSprings,
              paddingLeft: `${ICON_CONTAINER_INITIAL_WIDTH + 12}px`, // icon column width + gap-3
            }}
            ref={descriptionRef}
          >
            {typeof description === "function"
              ? description(updateHeight, hovered)
              : description}
          </animated.div>
        )
      )}
    </div>
  );
};
